<script setup>
import { reactive, ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength, helpers } from '@vuelidate/validators';
import V4Button from 'next/button/Button.vue';
import Select from 'next/select/Select.vue';
import InputNext from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  definition: {
    type: Object,
    default: null,
  },
});

const { t } = useI18n();

const isValidEventName = value => /^[a-z][a-z0-9_]*$/.test(value);
// Reports group by these names as the event's built-in record fields, so the API rejects
// properties named after them.
const RESERVED_PROPERTY_NAMES = [
  'user',
  'agent',
  'inbox',
  'contact',
  'conversation',
];

const PROPERTY_TYPES = computed(() => [
  {
    value: 'text',
    label: t(
      'WGPT_CUSTOM_REPORTS.EVENT_DEFINITIONS.FORM.PROP_TYPE_OPTIONS.TEXT'
    ),
  },
  {
    value: 'number',
    label: t(
      'WGPT_CUSTOM_REPORTS.EVENT_DEFINITIONS.FORM.PROP_TYPE_OPTIONS.NUMBER'
    ),
  },
  {
    value: 'boolean',
    label: t(
      'WGPT_CUSTOM_REPORTS.EVENT_DEFINITIONS.FORM.PROP_TYPE_OPTIONS.BOOLEAN'
    ),
  },
  {
    value: 'conversation',
    label: t(
      'WGPT_CUSTOM_REPORTS.EVENT_DEFINITIONS.FORM.PROP_TYPE_OPTIONS.CONVERSATION'
    ),
  },
  {
    value: 'contact',
    label: t(
      'WGPT_CUSTOM_REPORTS.EVENT_DEFINITIONS.FORM.PROP_TYPE_OPTIONS.CONTACT'
    ),
  },
  {
    value: 'agent',
    label: t(
      'WGPT_CUSTOM_REPORTS.EVENT_DEFINITIONS.FORM.PROP_TYPE_OPTIONS.AGENT'
    ),
  },
  {
    value: 'inbox',
    label: t(
      'WGPT_CUSTOM_REPORTS.EVENT_DEFINITIONS.FORM.PROP_TYPE_OPTIONS.INBOX'
    ),
  },
]);

const formState = reactive({ name: '', description: '', properties: [] });
const propertyErrors = ref([]);

const isEditing = computed(() => !!props.definition?.id);

const rules = {
  name: {
    required: helpers.withMessage(
      () =>
        t(
          'WGPT_CUSTOM_REPORTS.EVENT_DEFINITIONS.FORM.VALIDATION.NAME_REQUIRED'
        ),
      required
    ),
    minLength: helpers.withMessage(
      () =>
        t('WGPT_CUSTOM_REPORTS.EVENT_DEFINITIONS.FORM.VALIDATION.NAME_LENGTH'),
      minLength(3)
    ),
    isValidEventName: helpers.withMessage(
      () =>
        t('WGPT_CUSTOM_REPORTS.EVENT_DEFINITIONS.FORM.VALIDATION.NAME_FORMAT'),
      isValidEventName
    ),
  },
};

const v$ = useVuelidate(rules, formState);

const nameError = computed(() =>
  v$.value.name.$error ? v$.value.name.$errors[0]?.$message || '' : ''
);

const propertyNameError = index => propertyErrors.value[index] || '';

onMounted(() => {
  if (props.definition) {
    formState.name = props.definition.name || '';
    formState.description = props.definition.description || '';
    const schema = props.definition.schema?.properties || {};
    formState.properties = Object.entries(schema).map(([key, val]) => ({
      name: key,
      type: val.type || 'text',
      label: val.label || '',
      description: val.description || '',
    }));
    propertyErrors.value = formState.properties.map(() => '');
  }
});

function addProperty() {
  formState.properties.push({
    name: '',
    type: 'text',
    label: '',
    description: '',
  });
  propertyErrors.value.push('');
}

function removeProperty(index) {
  formState.properties.splice(index, 1);
  propertyErrors.value.splice(index, 1);
}

function buildSchema() {
  const schemaProps = {};
  formState.properties.forEach(prop => {
    if (prop.name.trim()) {
      schemaProps[prop.name.trim()] = {
        type: prop.type,
        label: prop.label,
        ...(prop.description ? { description: prop.description } : {}),
      };
    }
  });
  return { properties: schemaProps };
}

const state = computed(() => ({
  name: formState.name,
  description: formState.description,
  schema: buildSchema(),
}));

const validate = async () => {
  propertyErrors.value = formState.properties.map(prop => {
    const name = prop.name.trim();
    if (!name) {
      return t(
        'WGPT_CUSTOM_REPORTS.EVENT_DEFINITIONS.FORM.VALIDATION.PROP_NAME_REQUIRED'
      );
    }
    if (RESERVED_PROPERTY_NAMES.includes(name)) {
      return t(
        'WGPT_CUSTOM_REPORTS.EVENT_DEFINITIONS.FORM.VALIDATION.PROP_NAME_RESERVED'
      );
    }
    return '';
  });
  const isNameValid = await v$.value.$validate();
  const hasPropertyErrors = propertyErrors.value.some(Boolean);
  return isNameValid && !hasPropertyErrors;
};

defineExpose({ validate, state });
</script>

<template>
  <div class="flex flex-col gap-4">
    <InputNext
      v-model="formState.name"
      :label="t('WGPT_CUSTOM_REPORTS.EVENT_DEFINITIONS.FORM.NAME')"
      :placeholder="
        t('WGPT_CUSTOM_REPORTS.EVENT_DEFINITIONS.FORM.NAME_PLACEHOLDER')
      "
      :disabled="isEditing"
      :message="nameError"
      :message-type="nameError ? 'error' : 'info'"
      @input="v$.name.$touch"
    />

    <div>
      <label class="block text-sm font-medium text-n-slate-12 mb-1">
        {{ t('WGPT_CUSTOM_REPORTS.EVENT_DEFINITIONS.FORM.DESCRIPTION') }}
      </label>
      <textarea
        v-model="formState.description"
        :placeholder="
          t(
            'WGPT_CUSTOM_REPORTS.EVENT_DEFINITIONS.FORM.DESCRIPTION_PLACEHOLDER'
          )
        "
        class="w-full px-3 py-2 rounded-lg border border-n-strong bg-n-surface-1 text-n-slate-12 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-n-brand"
        rows="2"
      />
    </div>

    <div>
      <div class="flex items-center justify-between mb-3">
        <div>
          <h4 class="text-sm font-semibold text-n-slate-12">
            {{
              t('WGPT_CUSTOM_REPORTS.EVENT_DEFINITIONS.FORM.PROPERTIES_TITLE')
            }}
          </h4>
          <p class="text-xs text-n-slate-11 mt-0.5">
            {{
              t(
                'WGPT_CUSTOM_REPORTS.EVENT_DEFINITIONS.FORM.PROPERTIES_DESCRIPTION'
              )
            }}
          </p>
        </div>
        <V4Button
          xs
          ghost
          blue
          type="button"
          icon="i-lucide-plus"
          :label="t('WGPT_CUSTOM_REPORTS.EVENT_DEFINITIONS.FORM.ADD_PROPERTY')"
          @click="addProperty"
        />
      </div>

      <table
        class="min-w-full table-auto outline outline-1 -outline-offset-1 outline-n-strong rounded-xl"
      >
        <thead>
          <tr class="border-b border-n-strong">
            <th
              class="py-3 ltr:pl-4 rtl:pr-4 ltr:pr-4 rtl:pl-4 text-start text-heading-3 text-n-slate-12"
            >
              {{ t('WGPT_CUSTOM_REPORTS.EVENT_DEFINITIONS.FORM.PROP_NAME') }}
            </th>
            <th
              class="py-3 ltr:pr-4 rtl:pl-4 text-start text-heading-3 text-n-slate-12"
            >
              {{ t('WGPT_CUSTOM_REPORTS.EVENT_DEFINITIONS.FORM.PROP_TYPE') }}
            </th>
            <th
              class="py-3 ltr:pr-4 rtl:pl-4 text-start text-heading-3 text-n-slate-12"
            >
              {{ t('WGPT_CUSTOM_REPORTS.EVENT_DEFINITIONS.FORM.PROP_LABEL') }}
            </th>
            <th
              class="py-3 ltr:pr-4 rtl:pl-4 text-start text-heading-3 text-n-slate-12"
            >
              {{
                t('WGPT_CUSTOM_REPORTS.EVENT_DEFINITIONS.FORM.PROP_DESCRIPTION')
              }}
            </th>
            <th class="py-3 ltr:pr-4 rtl:pl-4" />
          </tr>
        </thead>
        <tbody>
          <tr v-if="formState.properties.length === 0" class="text-center">
            <td
              class="py-4 ltr:pl-4 ltr:pr-3 rtl:pl-3 rtl:pr-4 text-body-main"
              colspan="5"
            >
              <p class="text-xs text-center text-n-slate-10 italic py-3 w-full">
                {{
                  t('WGPT_CUSTOM_REPORTS.EVENT_DEFINITIONS.FORM.NO_PROPERTIES')
                }}
              </p>
            </td>
          </tr>
          <tr v-for="(prop, index) in formState.properties" :key="index">
            <td
              class="pt-4 pb-2 ltr:pl-4 ltr:pr-3 rtl:pl-3 rtl:pr-4 text-body-main align-top"
            >
              <InputNext
                v-model.trim="prop.name"
                type="text"
                :placeholder="
                  t(
                    'WGPT_CUSTOM_REPORTS.EVENT_DEFINITIONS.FORM.PROP_NAME_PLACEHOLDER'
                  )
                "
                :message="propertyNameError(index)"
                :message-type="propertyNameError(index) ? 'error' : 'info'"
              />
            </td>
            <td class="pt-4 pb-2 ltr:pr-3 rtl:pl-3 text-body-main align-top">
              <Select
                v-model="prop.type"
                :options="PROPERTY_TYPES"
                class="[&_select]:bg-n-alpha-black2"
              />
            </td>
            <td class="pt-4 pb-2 ltr:pr-3 rtl:pl-3 text-body-main align-top">
              <InputNext
                v-model.trim="prop.label"
                type="text"
                :placeholder="
                  t(
                    'WGPT_CUSTOM_REPORTS.EVENT_DEFINITIONS.FORM.PROP_LABEL_PLACEHOLDER'
                  )
                "
              />
            </td>
            <td class="pt-4 pb-2 ltr:pr-3 rtl:pl-3 text-body-main align-top">
              <InputNext
                v-model.trim="prop.description"
                type="text"
                :placeholder="
                  t(
                    'WGPT_CUSTOM_REPORTS.EVENT_DEFINITIONS.FORM.PROP_DESCRIPTION_PLACEHOLDER'
                  )
                "
              />
            </td>
            <td class="pt-4 pb-2 ltr:pr-4 rtl:pl-4 text-body-main align-top">
              <V4Button
                xs
                ghost
                slate
                type="button"
                icon="i-lucide-trash-2"
                class="my-2"
                @click="removeProperty(index)"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
